import type {
	CreateExperimentRequest,
	ExperimentDoc,
	ExperimentStatus,
	UpdateExperimentRequest,
} from '@orderlymeds/measurements-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiFetch } from './ApiProvider';

// Query keys
export const experimentKeys = {
	all: (projectId: string) => ['projects', projectId, 'experiments'] as const,
	detail: (projectId: string, expId: string) =>
		['projects', projectId, 'experiments', expId] as const,
};

export function useExperiments(projectId: string) {
	const apiFetch = useApiFetch();
	return useQuery({
		queryKey: experimentKeys.all(projectId),
		queryFn: () => apiFetch<ExperimentDoc[]>(`/api/admin/projects/${projectId}/experiments`),
		enabled: !!projectId,
		staleTime: 30_000,
	});
}

export function useExperiment(projectId: string, expId: string) {
	const apiFetch = useApiFetch();
	return useQuery({
		queryKey: experimentKeys.detail(projectId, expId),
		queryFn: () => apiFetch<ExperimentDoc>(`/api/admin/projects/${projectId}/experiments/${expId}`),
		enabled: !!projectId && !!expId,
		staleTime: 30_000,
	});
}

export function useCreateExperiment(projectId: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateExperimentRequest) =>
			apiFetch<ExperimentDoc>(`/api/admin/projects/${projectId}/experiments`, {
				method: 'POST',
				body: JSON.stringify(data),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: experimentKeys.all(projectId) });
		},
	});
}

export function useUpdateExperiment(projectId: string, expId: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: UpdateExperimentRequest) =>
			apiFetch<ExperimentDoc>(`/api/admin/projects/${projectId}/experiments/${expId}`, {
				method: 'PUT',
				body: JSON.stringify(data),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: experimentKeys.all(projectId) });
			queryClient.invalidateQueries({ queryKey: experimentKeys.detail(projectId, expId) });
		},
	});
}

export function useChangeExperimentStatus(projectId: string, expId: string) {
	const apiFetch = useApiFetch();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (status: ExperimentStatus) =>
			apiFetch<ExperimentDoc>(`/api/admin/projects/${projectId}/experiments/${expId}/status`, {
				method: 'PATCH',
				body: JSON.stringify({ status }),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: experimentKeys.all(projectId) });
			queryClient.invalidateQueries({ queryKey: experimentKeys.detail(projectId, expId) });
		},
	});
}
